/** Standard app chrome: banner (if break-glass) → header → sidebar + main content. */

import { useMemo, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { AppHeader } from './AppHeader';
import { LocalAdminBanner } from './LocalAdminBanner';
import { Sidebar } from './Sidebar';
import { CommandPalette } from '@/components/common/CommandPalette';
import { PwaStatus } from '@/components/common/PwaStatus';
import { ShortcutSheet } from '@/components/common/ShortcutSheet';
import { NewTicketDialog } from '@/components/tickets/NewTicketDialog';
import { useAuth } from '@/contexts/AuthContext';
import { useHotkeys, type Hotkey } from '@/hooks/useHotkeys';
import { useRealtime } from '@/hooks/useRealtime';

/** How long a `g` prefix stays armed before it's treated as a stray keypress. */
const SEQUENCE_MS = 1_000;

export function AppLayout() {
  // One socket for the whole app; mounted here because AppLayout is the single authenticated
  // shell every screen renders inside.
  useRealtime();

  const { user, isLocalAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const sequenceRef = useRef<{ key: string; at: number } | null>(null);

  const hotkeys = useMemo<Hotkey[]>(
    () => [
      // The only binding allowed to fire from inside a field: Ctrl-K is unambiguous and is
      // exactly what people reach for while their cursor is in a search box.
      { key: 'k', meta: true, allowInInput: true, handler: () => setPaletteOpen(true) },
      { key: '?', handler: () => setShortcutsOpen(true) },
      { key: 'n', handler: () => setNewTicketOpen(true) },
      {
        key: 'g',
        handler: () => {
          sequenceRef.current = { key: 'g', at: Date.now() };
        },
      },
      // Vim-style `g`-prefixed jumps. The prefix expires so a stray `g` doesn't silently arm a
      // navigation that fires minutes later when the user types a `t` somewhere else.
      {
        key: 't',
        handler: () => {
          const armed = sequenceRef.current;
          if (armed?.key === 'g' && Date.now() - armed.at < SEQUENCE_MS) {
            sequenceRef.current = null;
            navigate('/tickets');
          }
        },
      },
      {
        key: 'c',
        handler: () => {
          const armed = sequenceRef.current;
          if (armed?.key === 'g' && Date.now() - armed.at < SEQUENCE_MS) {
            sequenceRef.current = null;
            navigate('/companies');
          }
        },
      },
    ],
    [navigate],
  );

  useHotkeys(hotkeys);

  return (
    <div className="h-full flex flex-col bg-surface-50 dark:bg-surface-950">
      <PwaStatus />
      {isLocalAdmin ? <LocalAdminBanner appName={user?.app_name ?? 'Citara Central'} /> : null}

      <AppHeader
        onToggleSidebar={() => setSidebarOpen((open) => !open)}
        onOpenPalette={() => setPaletteOpen(true)}
      />

      <div className="flex-1 flex min-h-0">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <main className="flex-1 overflow-y-auto">
          {/* max-w-7xl centred content with the standard padding. */}
          <div className="max-w-7xl mx-auto px-4 py-6">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNewTicket={() => setNewTicketOpen(true)}
      />
      <ShortcutSheet open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <NewTicketDialog open={newTicketOpen} onClose={() => setNewTicketOpen(false)} />
    </div>
  );
}
